/* eslint-disable no-unused-vars */
import React, { FC, memo, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography
} from '@material-ui/core';
import { colors } from 'src/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Props {
  status: string;
  label?: string;
  headerColor?: string;
  tickets: any[] | [];
  totalCount?: number;
  totalDealValue?: number;
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: (status: string) => void;
  renderTicket: (ticket: any) => React.ReactNode;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 320,
    flexShrink: 0,
    minHeight: 650,
    maxHeight: 650,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.grey[100]
  },
  header: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  headerLabel: {
    color: '#000000',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  countChip: {
    backgroundColor: colors.grey[300],
    color: '#000000',
    fontWeight: 700,
    height: 24
  },
  valueText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    lineHeight: 1.2,
    textAlign: 'left'
  },
  headerMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1.5)
  }
}));

const formatMoney = (value: number) =>
  `₱${Number(value || 0).toLocaleString('en-PH', {
    maximumFractionDigits: 0
  })}`;

// ─── Component ───────────────────────────────────────────────────────────────

const component: FC<Props> = ({
  status,
  label,
  headerColor = '#757575',
  tickets,
  totalCount,
  totalDealValue = 0,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  renderTicket
}) => {
  const classes = useStyles();
  const scrollRef = useRef<HTMLDivElement>(null);

  const countLabel =
    totalCount !== undefined
      ? `${tickets.length} / ${totalCount}`
      : `${tickets.length}`;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || isLoading || !hasMore) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    if (distanceFromBottom <= 10) {
      onLoadMore?.(status);
    }
  }, [isLoading, hasMore, onLoadMore, status]);

  return (
    <Paper className={classes.root} elevation={2}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="subtitle2" className={classes.headerLabel}>
          {label ?? status}
        </Typography>
        <Box className={classes.headerMeta}>
          <Chip
            label={`Deals ${countLabel}`}
            size="small"
            className={classes.countChip}
          />
        </Box>
      </Box>
      <div style={{ paddingRight: 15, paddingLeft: 15, paddingBottom: 15 }}>
        <Typography className={classes.valueText}>
          Total value: {formatMoney(totalDealValue)}
        </Typography>
      </div>
      <Divider style={{ marginLeft: 15, marginRight: 15, marginBottom: 10 }} />

      {/* Ticket list */}
      <div
        ref={scrollRef}
        className={classes.scrollArea}
        onScroll={handleScroll}
      >
        {tickets.length === 0 && !isLoading ? (
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            style={{ padding: '32px 0' }}
          >
            No tickets
          </Typography>
        ) : (
          tickets.map((ticket) => (
            <Box key={ticket.id}>{renderTicket(ticket)}</Box>
          ))
        )}

        {isLoading && (
          <Box className={classes.loader}>
            <CircularProgress size={20} />
          </Box>
        )}
      </div>
    </Paper>
  );
};

export const CrmColumns = memo(component);
